import { styled } from '@linaria/react';
import { Group, Stack } from '@mantine/core';
import type { MinifiedTask } from '../../../../../api/tasks-client/task-client.type';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../../helpers/date-time-helpers/formatting/formatting';
import { capitalizeFirstLetter } from '../../../../../helpers/string-helpers/string-helpers';
import { resolveTaskTypeLabel } from '../../../../../helpers/task-type-labels/task-type-labels';
import { inkLight, secondaryBase, skyLightest } from '../../../../../styles/design-tokens';
import { Divider } from '../../../../../ui-library/divider/divider';
import { Link } from '../../../../../ui-library/link/link';
import { Text } from '../../../../../ui-library/typography/typography';
import { formatPoints } from './side-section-work-units.helpers';

type Props = {
  task: MinifiedTask;
};

export const SideSectionWorkUnitsItem = ({ task }: Props) => {
  const { t } = useTranslation();
  const formattedCreatedDate = formatDate(task.created_date, { format: 'MMM D, YYYY' });
  const formattedType = !task.type
    ? '-'
    : task.type.toLowerCase() === 'unknown'
      ? (resolveTaskTypeLabel(task) ?? capitalizeFirstLetter(task.type))
      : capitalizeFirstLetter(task.type);
  const formattedPoints = formatPoints(task.points, t);

  return (
    <Container>
      <Stack>
        <Stack gap={8}>
          <Link href={task.url}>
            <Text size="tiny" weight="bold" color={secondaryBase}>
              {task.title}
            </Text>
          </Link>
          <Text size="tiny" weight="medium" lineHeight="tight">
            {task.name}
          </Text>
        </Stack>
        <Divider />
        <Stack gap={4}>
          <Group gap={8} justify="space-between">
            <Text size="tiny" color={inkLight}>
              {t('process_analysis.work_units.created')}
            </Text>
            <Text size="tiny">{formattedCreatedDate}</Text>
          </Group>
          <Group gap={8} justify="space-between">
            <Text size="tiny" color={inkLight}>
              {t('process_analysis.work_units.type')}
            </Text>
            <Text size="tiny">{formattedType}</Text>
          </Group>
          <Group gap={8} justify="space-between">
            <Text size="tiny" color={inkLight}>
              {t('process_analysis.work_units.points', { estimation: t('common.estimation') })}
            </Text>
            <Text size="tiny">{formattedPoints}</Text>
          </Group>
        </Stack>
      </Stack>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 16px;
  background-color: ${skyLightest};
  border-radius: 8px;
`;
